"use client";

import React from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { Zap } from "lucide-react";

export interface TalktribePreviewBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    imageUrl?: string;
    imageAlt?: string;
    domainLabel?: string;
    highlights?: string[];
}

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 },
};

export default function TalktribePreviewBlock({
    tag = "Conversation Interface",
    title = "Natural, Fluid Conversations",
    description = "The chat interface is designed for immersive character interactions. Characters respond in-character with personality-consistent dialogue. The UI supports real-time streaming responses, message history, and seamless language switching mid-conversation.",
    imageUrl = "/images/talktribe_chat.png",
    imageAlt = "TalkTribe Chat Interface",
    domainLabel = "talktribe.app/chat/sakura-aoi",
    highlights = [
        "Real-time streaming responses",
        "In-character personality consistency",
        "Conversation history & recall",
        "Mid-chat language switching"
    ]
}: TalktribePreviewBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#09090b] border-t border-b border-zinc-900 w-full" data-header-theme="light">
            <div className="container mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <div className="lg:col-span-7 flex justify-center order-2 lg:order-1">
                        <motion.div {...fadeUp} className="relative w-full max-w-xl rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-950/80 p-2.5 shadow-[0_25px_60px_rgba(0,0,0,0.8)]">
                            <div className="w-full h-7 bg-zinc-900 border border-zinc-800 rounded-t-lg flex items-center px-4 gap-2 mb-2">
                                <div className="w-2 h-2 rounded-full bg-red-500/80" />
                                <div className="w-2 h-2 rounded-full bg-yellow-500/80" />
                                <div className="w-2 h-2 rounded-full bg-green-500/80" />
                                <div className="h-4 bg-zinc-950 px-6 rounded text-[9px] font-mono text-zinc-500 flex items-center mx-auto border border-zinc-900">
                                    {domainLabel}
                                </div>
                            </div>
                            <div className="relative w-full aspect-[960/457] rounded-b-lg overflow-hidden border border-zinc-900 bg-[#070319]">
                                <Image
                                    src={imageUrl}
                                    alt={imageAlt}
                                    fill
                                    sizes="(max-width: 1024px) 100vw, 58vw"
                                    className="object-contain object-center"
                                />
                            </div>
                        </motion.div>
                    </div>

                    <div className="lg:col-span-5 order-1 lg:order-2 space-y-6">
                        {tag && (
                            <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest block">// {tag}</span>
                        )}
                        <h2 className="text-3xl md:text-4xl font-normal text-white">{title}</h2>
                        <p className="text-zinc-400 text-sm leading-relaxed font-light">
                            {description}
                        </p>
                        {highlights && highlights.length > 0 && (
                            <div className="pt-4 border-t border-zinc-900 space-y-3">
                                {highlights.map((item, i) => (
                                    <div key={i} className="flex items-center gap-3 text-xs text-zinc-400">
                                        <Zap className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
